/**
 * Shared procedural PBR material library.
 *
 * Every recipe derives albedo, normal and packed roughness/metalness from one periodic height
 * field through bakeSurface(). Materials may vary their tint, but the structural response remains
 * shared: cloth colour cannot drift away from its weave and worn paint cannot expose metal in a
 * place where its normal map says the surface is untouched.
 */

import * as THREE from 'three';
import { bakeSurface, clearScratchPool } from './bake.js';
import {
  clamp01, mix, smoothstep, tileBillow2, tileFbm2, tileFracture2, tileRidged2,
  tileStreak2, tileValue2, treadDiamond2,
} from './noise.js';

const RECIPE_NAMES = Object.freeze([
  'skin', 'cloth', 'leather', 'rubber', 'paintedMetal', 'brushedMetal', 'concrete',
]);

const BASE_OPTIONS = Object.freeze({
  skin: Object.freeze({ roughness: 0.72, metalness: 0, normalScale: 0.55 }),
  cloth: Object.freeze({ roughness: 1, metalness: 0, normalScale: 0.72 }),
  leather: Object.freeze({ roughness: 0.82, metalness: 0, normalScale: 0.58 }),
  rubber: Object.freeze({ roughness: 0.95, metalness: 0, normalScale: 0.68 }),
  paintedMetal: Object.freeze({ roughness: 0.72, metalness: 0.86, normalScale: 0.48 }),
  brushedMetal: Object.freeze({ roughness: 0.58, metalness: 0.94, normalScale: 0.38 }),
  concrete: Object.freeze({ roughness: 0.96, metalness: 0.08, normalScale: 0.8 }),
});

function neutralAlbedo(out, value, cool = 0) {
  out[0] = clamp01(value - cool * 0.18);
  out[1] = clamp01(value - cool * 0.04);
  out[2] = clamp01(value + cool * 0.12);
}

function recipes(size, anisotropy) {
  const common = {
    size,
    anisotropy,
    outputs: { map: 1, normalMap: 1, ormMap: 2 },
    ao: { strength: 0.72, radiusScale: 0.7, divisor: 2 },
  };
  return {
    skin: {
      ...common,
      name: 'coi.skin',
      seed: 0x5101,
      repeat: 1,
      height: (u, v) => {
        const pores = tileBillow2(u, v, {
          freqX: 30, freqY: 36, octaves: 2, gain: 0.46, seed: 0x5101,
        });
        const tissue = tileFbm2(u, v, {
          freqX: 5, freqY: 7, octaves: 3, gain: 0.5, seed: 0x5102,
        });
        return 0.5 + (tissue - 0.5) * 0.2 + (pores - 0.5) * 0.09;
      },
      albedo: (out, s) => neutralAlbedo(out, 0.9 + (s.h - 0.5) * 0.11, 0.015),
      roughness: (s) => 0.51 + (1 - s.h) * 0.11,
      metalness: () => 0,
      roughRange: 0.72,
      metalRange: 1,
      normal: { heightScale: 0.008, strength: 0.72 },
    },
    cloth: {
      ...common,
      name: 'coi.cloth',
      seed: 0xc107,
      repeat: 1,
      height: (u, v) => {
        const warp = Math.pow(Math.abs(Math.sin(u * Math.PI * 32)), 5);
        const weft = Math.pow(Math.abs(Math.sin(v * Math.PI * 28)), 5);
        const slub = tileFbm2(u, v, {
          freqX: 8, freqY: 7, octaves: 2, gain: 0.45, seed: 0xc107,
        });
        return 0.39 + (warp + weft) * 0.18 + (slub - 0.5) * 0.08;
      },
      albedo: (out, s) => neutralAlbedo(out, 0.88 + (s.h - 0.5) * 0.16, 0.03),
      roughness: (s) => 0.79 + (1 - s.h) * 0.13,
      metalness: () => 0,
      roughRange: 1,
      metalRange: 1,
      normal: { heightScale: 0.012, strength: 0.84 },
    },
    leather: {
      ...common,
      name: 'coi.leather',
      seed: 0x1ea7,
      repeat: 1,
      height: (u, v) => {
        const grain = tileRidged2(u, v, {
          freqX: 11, freqY: 14, octaves: 3, gain: 0.48, sharpness: 1.6, seed: 0x1ea7,
        });
        const crease = tileStreak2(u, v, {
          across: 38, along: 3, octaves: 2, gain: 0.45, shear: 1, seed: 0x1ea8,
        });
        return 0.45 + (grain - 0.5) * 0.22 + (crease - 0.5) * 0.06;
      },
      wear: (_u, _v, s) => smoothstep(0.1, 0.42, Math.max(0, -s.curvature) + s.slope),
      albedo: (out, s) => neutralAlbedo(out, 0.84 + s.wear * 0.1 + (s.h - 0.5) * 0.08, 0.01),
      roughness: (s) => 0.64 - s.wear * 0.12 + (1 - s.h) * 0.06,
      metalness: () => 0,
      roughRange: 0.82,
      metalRange: 1,
      normal: { heightScale: 0.014, strength: 0.76 },
    },
    rubber: {
      ...common,
      name: 'coi.rubber',
      seed: 0x7abb,
      repeat: 1,
      height: (u, v) => {
        const tread = treadDiamond2(u, v, 7, 10, 2.5);
        const grain = tileValue2(u, v, 24, 24, 0x7abb);
        return 0.36 + tread * 0.3 + (grain - 0.5) * 0.06;
      },
      albedo: (out, s) => neutralAlbedo(out, 0.72 + s.h * 0.13, 0.015),
      roughness: (s) => 0.79 + (1 - s.h) * 0.12,
      metalness: () => 0,
      roughRange: 0.95,
      metalRange: 1,
      normal: { heightScale: 0.018, strength: 0.8 },
    },
    paintedMetal: {
      ...common,
      name: 'coi.painted-metal',
      seed: 0x9a17,
      repeat: 1,
      height: (u, v) => {
        const orangePeel = tileBillow2(u, v, {
          freqX: 18, freqY: 18, octaves: 3, gain: 0.45, seed: 0x9a17,
        });
        const scratches = tileStreak2(u, v, {
          across: 52, along: 3, octaves: 2, gain: 0.42, shear: 1, seed: 0x9a18,
        });
        return 0.48 + (orangePeel - 0.5) * 0.16 + (scratches - 0.5) * 0.035;
      },
      wear: (_u, _v, s) => clamp01(
        smoothstep(0.08, 0.34, Math.max(0, -s.curvature) + s.slope * 0.7)
        * (0.35 + tileValue2(s.u, s.v, 5, 5, 0x9a19) * 0.65),
      ),
      albedo: (out, s) => neutralAlbedo(out, mix(0.9, 0.72, s.wear), 0.025),
      roughness: (s) => mix(0.46, 0.29, s.wear),
      metalness: (s) => mix(0.12, 0.82, s.wear),
      roughRange: 0.72,
      metalRange: 0.86,
      normal: { heightScale: 0.01, strength: 0.65 },
    },
    brushedMetal: {
      ...common,
      name: 'coi.brushed-metal',
      seed: 0xb205,
      repeat: 1,
      height: (u, v) => {
        const brush = tileStreak2(u, v, {
          across: 58, along: 2, octaves: 3, gain: 0.5, shear: 0, seed: 0xb205,
        });
        const dents = tileFbm2(u, v, {
          freqX: 5, freqY: 5, octaves: 2, gain: 0.45, seed: 0xb206,
        });
        return 0.49 + (brush - 0.5) * 0.13 + (dents - 0.5) * 0.05;
      },
      albedo: (out, s) => neutralAlbedo(out, 0.84 + (s.h - 0.5) * 0.18, 0.05),
      roughness: (s) => 0.3 + (1 - s.h) * 0.12,
      metalness: () => 0.9,
      roughRange: 0.58,
      metalRange: 0.94,
      normal: { heightScale: 0.007, strength: 0.62 },
    },
    concrete: {
      ...common,
      name: 'coi.concrete',
      seed: 0xc0c2,
      repeat: 4,
      height: (u, v) => {
        const aggregate = tileBillow2(u, v, {
          freqX: 13, freqY: 13, octaves: 4, gain: 0.48, seed: 0xc0c2,
        });
        const pits = tileFracture2(u, v, {
          freq: 5, octaves: 2, width: 0.035, gain: 0.46, warp: 0.02, seed: 0xc0c3,
        });
        return 0.49 + (aggregate - 0.5) * 0.24 - pits * 0.13;
      },
      dirt: (_u, _v, s) => clamp01((0.52 - s.h) * 3.2 + (1 - s.ao) * 0.5),
      wet: (u, v, s) => clamp01(
        smoothstep(0.44, 0.55, 0.52 - s.h + tileFbm2(u, v, {
          freqX: 3, freqY: 4, octaves: 2, gain: 0.5, seed: 0xc0c4,
        }) * 0.14),
      ),
      albedo: (out, s) => neutralAlbedo(out, 0.79 - s.dirt * 0.13 - s.wet * 0.07, 0.07),
      roughness: (s) => 0.84 - s.wet * 0.31 + s.dirt * 0.06,
      metalness: () => 0.02,
      roughRange: 0.96,
      metalRange: 0.08,
      normal: { heightScale: 0.018, strength: 0.85 },
      ao: { strength: 0.9, radiusScale: 0.85, divisor: 2 },
    },
  };
}

function stableKey(kind, options) {
  const keys = Object.keys(options).sort();
  let out = kind;
  for (const key of keys) {
    const value = options[key];
    if (value === undefined || typeof value === 'object') continue;
    out += `|${key}:${String(value)}`;
  }
  return out;
}

export class MaterialLibrary {
  constructor({ size = 128, anisotropy = 4 } = {}) {
    this.size = Math.max(32, Math.round(size));
    this.anisotropy = Math.max(1, Math.round(anisotropy));
    this.surfaces = new Map();
    this.materials = new Map();
    this.textureBytes = 0;
    this.disposed = false;
  }

  bake() {
    if (this.disposed) throw new Error('MaterialLibrary has been disposed.');
    if (this.surfaces.size) return this;
    const specs = recipes(this.size, this.anisotropy);
    for (const name of RECIPE_NAMES) {
      const surface = bakeSurface(specs[name]);
      this.surfaces.set(name, surface);
      this.textureBytes += surface.bytes;
    }
    clearScratchPool();
    return this;
  }

  /**
   * Returns a cached material instance. Texture topology is fixed before renderer prewarm; callers
   * may animate uniform values such as emissiveIntensity but must not replace maps during combat.
   */
  material(kind, options = {}) {
    if (!this.surfaces.size) this.bake();
    const surface = this.surfaces.get(kind);
    if (!surface) throw new Error(`Unknown procedural material "${kind}".`);
    const key = stableKey(kind, options);
    const cached = this.materials.get(key);
    if (cached) return cached;
    const base = BASE_OPTIONS[kind];
    const physical = options.physical === true;
    const params = {
      color: options.color !== undefined ? options.color : 0xffffff,
      map: surface.map,
      normalMap: options.normalMap === false ? null : surface.normalMap,
      normalScale: new THREE.Vector2(base.normalScale, base.normalScale),
      roughness: surface.roughRange,
      roughnessMap: options.packedMaps === false ? null : surface.ormMap,
      metalness: surface.metalRange,
      metalnessMap: options.packedMaps === false ? null : surface.ormMap,
      emissive: options.emissive !== undefined ? options.emissive : 0x000000,
      emissiveIntensity: options.emissiveIntensity || 0,
      transparent: options.transparent === true,
      opacity: options.opacity !== undefined ? options.opacity : 1,
      depthWrite: options.depthWrite !== false,
      side: options.side !== undefined ? options.side : THREE.FrontSide,
      vertexColors: options.vertexColors === true,
    };
    if (physical) {
      params.clearcoat = options.clearcoat || 0;
      params.clearcoatRoughness = options.clearcoatRoughness !== undefined
        ? options.clearcoatRoughness : 0.25;
    }
    const material = physical
      ? new THREE.MeshPhysicalMaterial(params)
      : new THREE.MeshStandardMaterial(params);
    material.name = options.name || `coi:${kind}`;
    material.userData.surfaceKind = kind;
    this.materials.set(key, material);
    return material;
  }

  stats() {
    return {
      recipes: this.surfaces.size,
      materials: this.materials.size,
      textureBytes: this.textureBytes,
      size: this.size,
      anisotropy: this.anisotropy,
    };
  }

  dispose() {
    if (this.disposed) return;
    for (const material of this.materials.values()) material.dispose();
    for (const surface of this.surfaces.values()) {
      for (const texture of surface.textures) texture.dispose();
    }
    this.materials.clear();
    this.surfaces.clear();
    clearScratchPool();
    this.disposed = true;
  }
}
